import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

const CLIENT_TYPE_OPTIONS = [
  {
    id: 'INDIVIDUAL',
    name: 'Individual User',
    description: 'Sign up as an individual user without an organization',
    icon: '👤',
    features: [
      'Personal dashboard',
      'Access to public templates',
      'Can join organizations later'
    ],
    accessLevel: 'Individual access',
    color: 'purple'
  },
  {
    id: 'TENANT_ADMIN',
    name: 'Tenant Administrator',
    description: 'Create and manage a new tenant organization',
    icon: '🏢',
    features: [
      'Full tenant administration',
      'Invite team members',
      'Manage organizational settings'
    ],
    accessLevel: 'Tenant administration',
    color: 'blue'
  },
  {
    id: 'TENANT_USER',
    name: 'Tenant User',
    description: 'Join an existing tenant organization',
    icon: '👥',
    features: [
      'Access to tenant resources',
      'Collaborate with team members',
      'Use tenant-specific templates'
    ],
    accessLevel: 'Tenant user access',
    color: 'green'
  },
  {
    id: 'CONSULTANT',
    name: 'Consultant Organization',
    description: 'Create a consultant organization to serve clients',
    icon: '💼',
    features: [
      'Create and sell templates',
      'Manage multiple client relationships',
      'Access to consultant dashboard'
    ],
    accessLevel: 'Consultant access',
    color: 'orange'
  },
  {
    id: 'SUPER_TENANT_ADMIN',
    name: 'Super Tenant Administrator',
    description: 'Create a parent company to oversee portfolio companies',
    icon: '🏛️',
    features: [
      'Oversee multiple portfolio companies',
      'Create templates for Portcos',
      'View all Portco dashboards',
      'Extract data from Portcos'
    ],
    accessLevel: 'Super Tenant administration',
    color: 'indigo'
  },
  {
    id: 'PORTCO_ADMIN',
    name: 'Portfolio Company Administrator',
    description: 'Join as administrator of a portfolio company',
    icon: '🏭',
    features: [
      'Manage Portco users and roles',
      'Create Portco-specific structures',
      'Customize inherited templates',
      'Access to inherited templates'
    ],
    accessLevel: 'Portco administration',
    color: 'teal'
  },
  {
    id: 'PORTCO_USER',
    name: 'Portfolio Company User',
    description: 'Join as a user in a portfolio company',
    icon: '👷',
    features: [
      'Access to Portco resources',
      'Use inherited templates',
      'Collaborate within Portco',
      'Limited administrative access'
    ],
    accessLevel: 'Portco user access',
    color: 'cyan'
  }
];

export const ClientTypeSelectionStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [selectedClientType, setSelectedClientType] = useState<'INDIVIDUAL' | 'TENANT_ADMIN' | 'TENANT_USER' | 'CONSULTANT' | 'SUPER_TENANT_ADMIN' | 'PORTCO_ADMIN' | 'PORTCO_USER' | ''>(data.clientType || '');

  const handleClientTypeSelect = (clientType: 'INDIVIDUAL' | 'TENANT_ADMIN' | 'TENANT_USER' | 'CONSULTANT' | 'SUPER_TENANT_ADMIN' | 'PORTCO_ADMIN' | 'PORTCO_USER') => {
    setSelectedClientType(clientType);
  };

  const handleNext = () => {
    if (selectedClientType) {
      onNext({ clientType: selectedClientType });
    }
  };

  const canGoNext = selectedClientType !== '';

  return (
    <OnboardingStep
      title={t('onboarding.clientType.title')}
      description={t('onboarding.clientType.description')}
      onNext={handleNext}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={false}
    >
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('onboarding.clientType.chooseType')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('onboarding.clientType.chooseDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CLIENT_TYPE_OPTIONS.map((option) => (
            <div
              key={option.id}
              className={`relative rounded-xl border-2 p-8 cursor-pointer transition-all duration-300 ${
                selectedClientType === option.id
                  ? `border-${option.color}-500 bg-${option.color}-50 ring-2 ring-${option.color}-500 ring-opacity-50 shadow-lg`
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              }`}
              onClick={() => handleClientTypeSelect(option.id as 'INDIVIDUAL' | 'TENANT_ADMIN' | 'TENANT_USER' | 'CONSULTANT' | 'SUPER_TENANT_ADMIN' | 'PORTCO_ADMIN' | 'PORTCO_USER')}
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-start mb-6">
                  <div className="flex-shrink-0">
                    <div className="text-4xl mb-4">{option.icon}</div>
                  </div>
                  <div className="flex-1 ml-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {option.name}
                    </h3>
                    <p className="text-gray-600 mb-3">
                      {option.description}
                    </p>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      option.color === 'blue' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {option.accessLevel}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {selectedClientType === option.id && (
                      <div className={`flex items-center justify-center w-8 h-8 bg-${option.color}-600 rounded-full`}>
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    {t('onboarding.clientType.features')}:
                  </h4>
                  <ul className="space-y-2">
                    {option.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedClientType && (
          <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('onboarding.clientType.selected')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  {selectedClientType === 'INDIVIDUAL' 
                    ? t('onboarding.clientType.individualSelected')
                    : selectedClientType === 'TENANT_ADMIN'
                    ? t('onboarding.clientType.tenantAdminSelected')
                    : selectedClientType === 'TENANT_USER'
                    ? t('onboarding.clientType.tenantUserSelected')
                    : selectedClientType === 'CONSULTANT'
                    ? t('onboarding.clientType.consultantSelected')
                    : selectedClientType === 'SUPER_TENANT_ADMIN'
                    ? t('onboarding.clientType.superTenantAdminSelected')
                    : selectedClientType === 'PORTCO_ADMIN'
                    ? t('onboarding.clientType.portcoAdminSelected')
                    : selectedClientType === 'PORTCO_USER'
                    ? t('onboarding.clientType.portcoUserSelected')
                    : ''
                  }
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </OnboardingStep>
  );
};
